import { useCallback, useEffect, useState } from "react";

export function useStoredList(storageKey: string, changeEvent: string) {
  const read = useCallback((): string[] => {
    try {
      const raw = localStorage.getItem(storageKey);
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? (parsed as string[]) : [];
    } catch {
      return [];
    }
  }, [storageKey]);

  const [items, setItems] = useState<string[]>(read);

  useEffect(() => {
    setItems(read());
    const sync = () => setItems(read());
    window.addEventListener(changeEvent, sync);
    window.addEventListener("storage", sync);
    return () => {
      window.removeEventListener(changeEvent, sync);
      window.removeEventListener("storage", sync);
    };
  }, [read, changeEvent]);

  const toggle = useCallback(
    (id: string) => {
      const current = read();
      const next = current.includes(id) ? current.filter((item) => item !== id) : [...current, id];
      try {
        localStorage.setItem(storageKey, JSON.stringify(next));
      } catch {
        // ignore storage write failures
      }
      setItems(next);
      window.dispatchEvent(new Event(changeEvent));
    },
    [read, storageKey, changeEvent],
  );

  const has = useCallback((id: string) => items.includes(id), [items]);

  return { items, toggle, has };
}